import { queryListings } from '../../ducks/SdkDataAccessLayer.duck';
import { isOtherUserBlocked } from '../../util/user';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import config from '../../config';

export const LISTINGS_INITIAL_NUM = 18;
export const LISTINGS_NUM_PER_PAGE = 9;

const { listingTypeHorse, listingTypeRider } = config;

const commonParams = {
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
        'variants.landscape-crop',
        'variants.landscape-crop2x',
        'variants.square-small',
    ],
};

const getRiderListing = async ({ representationListingId, visitorRepresentationId, dispatch }) => {
    const riderListingAuthor = !representationListingId ? visitorRepresentationId : null;

    if (!riderListingAuthor) return {};

    const riderListingsParams = {
        authorId: riderListingAuthor,
        pub_type: listingTypeRider,
        ...commonParams,
    };

    const riderListingsResponse = await dispatch(
        queryListings({
            include: ['author', 'images'],
            'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
            ...riderListingsParams,
        })
    );

    dispatch(addMarketplaceEntities(riderListingsResponse));

    return { riderListing: riderListingsResponse.data.data[0] };
};

const getHorseListing = async ({
    dispatch,
    representationListingId,
    visitorRepresentationId,
    visitedEntryId,
    store,
}) => {
    const isRiderPage = !!representationListingId;
    const horseListingsParams = {
        ids: [isRiderPage ? visitorRepresentationId : visitedEntryId],
        pub_type: listingTypeHorse,
        ...commonParams,
    };
    const currentState = store.getState();
    const allListings = currentState?.marketplaceData?.entities?.listing || {};

    const listingId = horseListingsParams.ids[0];

    if (allListings[listingId]) {
        return { horseListing: allListings[listingId] };
    }

    const horseListingsResponse = await dispatch(
        queryListings({
            include: ['author', 'images'],
            'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
            ...horseListingsParams,
        })
    );
    dispatch(addMarketplaceEntities(horseListingsResponse));

    return { horseListing: horseListingsResponse.data.data[0] };
};

export const getVisitors = async props => {
    const {
        visitors,
        currentUser,
        representationListingId,
        perPage,
        setIndex,
        startingIndex,
        store,
    } = props;

    const visitorsToProcess = [...visitors].slice(startingIndex, startingIndex + perPage + 1);

    const promises = visitorsToProcess.map(async visitor => {
        try {
            const params = { ...props, ...visitor };
            const [riderResult, horseResult] = await Promise.all([
                getRiderListing(params),
                getHorseListing(params),
            ]);

            return {
                riderListing: riderResult.riderListing,
                horseListing: horseResult.horseListing,
                visitor,
            };
        } catch (e) {
            return null;
        }
    });

    const results = await Promise.allSettled(promises);
    const response = [];
    /** early exit logic index */
    let lastProcessedIndex = startingIndex - 1;

    for (const [indexInSlice, result] of results.entries()) {
        lastProcessedIndex = startingIndex + indexInSlice;

        if (result.status !== 'fulfilled' || result.value === null) {
            continue;
        }

        const { riderListing, horseListing, visitor } = result.value;

        const isRiderPage = !!representationListingId;
        const avatarEntry = isRiderPage ? horseListing : riderListing;
        const noData = !horseListing || !avatarEntry;

        const allUsers = store.getState().marketplaceData.entities.user || {};
        const allImages = store.getState().marketplaceData.entities.image || {};

        const authorId = avatarEntry?.relationships?.author?.data?.id?.uuid;
        const author = allUsers[authorId];
        const profileImage = allImages[author?.relationships?.profileImage?.data?.id?.uuid];

        const otherPartyIsBlocked = isOtherUserBlocked(currentUser, authorId);

        const isDeleted = avatarEntry?.attributes?.deleted;
        const isBanned = avatarEntry?.attributes?.banned;

        if (isDeleted || isBanned || noData || otherPartyIsBlocked) {
            continue;
        }

        response.push({
            avatarEntry: {
                ...avatarEntry,
                author: {
                    ...author,
                    profileImage,
                },
            },
            currentListing: horseListing,
            riderListing,
            ...visitor,
        });
        /** early exit logic */
        if (response.length === perPage + 1) {
            setIndex(lastProcessedIndex);
            return response;
        }
    }

    return response;
};
